import { Centrifuge } from 'centrifuge';

/**
 * Cliente de Centrifuge para gestionar conexiones WebSocket
 * Cada instancia representa una conexión independiente
 */
export class CentrifugeClient {
  constructor(channelId) {
    this.channelId = channelId;
    this.centrifuge = null;
    this.subscription = null;
    this.isConnected = false;
    this.messageCallbacks = [];
    
    console.log('🔧 CentrifugeClient created for channel:', channelId);
  }

  /**
   * Conectar a Centrifuge y suscribirse al canal
   */
  connect() {
    if (this.centrifuge) {
      console.warn('⚠️ Ya existe una conexión activa');
      return;
    }

    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM3MjIiLCJleHAiOjE4NDM4NDA4ODUsImlhdCI6MTc0Mzg0MDg4NX0.0rGHsBefg4XOxX9_YmmnbB_p2Fse8HVVnnHOyzMh6Lw';

    this.centrifuge = new Centrifuge('wss://c.roll.suricatta.es/connection/websocket', {
      token: token,
      getToken: () => {
        return Promise.resolve(token);
      },
      debug: true,
      minReconnectDelay: 1000,
      maxReconnectDelay: 20000,
      timeout: 5000,
      maxServerPingDelay: 10000
    });

    // Event handlers
    this.centrifuge.on('connected', (ctx) => {
      console.log('✅ Centrifuge connected:', ctx);
      this.isConnected = true;
      
      // Re-suscribirse si había una suscripción previa
      if (this.subscription && !this.subscription.state === 'subscribed') {
        this._subscribe();
      }
    });

    this.centrifuge.on('disconnected', (ctx) => {
      console.log('❌ Centrifuge disconnected:', ctx);
      this.isConnected = false;
    });

    this.centrifuge.on('error', (ctx) => {
      console.error('💥 Centrifuge error:', ctx);
    });

    // Conectar
    this.centrifuge.connect();

    // Suscribirse al canal
    this._subscribe();
  }

  /**
   * Suscribirse al canal
   * @private
   */
  _subscribe() {
    if (!this.centrifuge) {
      console.warn('⚠️ No hay conexión de Centrifuge');
      return;
    }

    const channel = `${this.channelId}-host`;
    
    // Si ya existe una suscripción, desuscribirse primero
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription.removeAllListeners();
    }

    console.log('📡 Subscribing to channel:', channel);

    this.subscription = this.centrifuge.newSubscription(channel, {
      since: null,
      recover: true,
      positioned: false,
      joinLeave: false,
      minResubscribeDelay: 100,
      maxResubscribeDelay: 5000
    });

    // Event handlers de la suscripción
    this.subscription.on('publication', (ctx) => {
      console.log('📨 Message received:', ctx.data);
      // Llamar a todos los callbacks registrados
      this.messageCallbacks.forEach(callback => callback(ctx.data));
    });

    this.subscription.on('subscribed', (ctx) => {
      console.log('✅ Subscribed to channel:', channel, ctx);
    });

    this.subscription.on('unsubscribed', (ctx) => {
      console.log('👋 Unsubscribed from channel:', channel, ctx);
    });

    this.subscription.on('error', (ctx) => {
      console.error('💥 Subscription error:', ctx);
    });

    // Suscribirse
    this.subscription.subscribe();
  }

  /**
   * Cambiar el canal de suscripción
   * @param {string} newChannelId - Nuevo ID del canal
   */
  changeChannel(newChannelId) {
    console.log(`🔄 Changing channel: ${this.channelId} -> ${newChannelId}`);
    this.channelId = newChannelId;
    
    // Re-suscribirse con el nuevo canal
    if (this.centrifuge) {
      this._subscribe();
    }
  }

  /**
   * Publicar un mensaje en el canal
   * @param {object} data - Datos a publicar
   */
  publish(data) {
    if (!this.subscription) {
      console.warn('⚠️ No hay suscripción activa para publicar');
      return Promise.reject('No subscription');
    }

    console.log('📤 Publishing message:', data);
    
    return this.subscription.publish(data)
      .then(() => {
        console.log('✅ Message published successfully');
      })
      .catch((err) => {
        console.error('❌ Error publishing message:', err);
        throw err;
      });
  }

  /**
   * Registrar un callback para cuando se reciben mensajes
   * @param {function} callback - Función a llamar cuando llega un mensaje
   * @returns {function} Función para desregistrar el callback
   */
  onMessage(callback) {
    this.messageCallbacks.push(callback);
    
    // Retornar función para desregistrar
    return () => {
      const index = this.messageCallbacks.indexOf(callback);
      if (index > -1) {
        this.messageCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Desconectar y limpiar recursos
   */
  disconnect() {
    console.log('👋 Disconnecting CentrifugeClient');
    
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription.removeAllListeners();
      this.subscription = null;
    }

    if (this.centrifuge) {
      this.centrifuge.disconnect();
      this.centrifuge = null;
    }

    this.isConnected = false;
    this.messageCallbacks = [];
  }

  /**
   * Obtener el estado de la conexión
   * @returns {boolean}
   */
  getConnectionStatus() {
    return this.isConnected;
  }
}
